import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { 
  ShoppingCart, 
  X, 
  Trash2, 
  FileText, 
  BookOpen,
  IndianRupee,
  ShoppingBag,
  ArrowRight
} from "lucide-react";

export default function CartDrawer() {
  const navigate = useNavigate();
  const { 
    cartItems, 
    isCartOpen, 
    setIsCartOpen, 
    removeFromCart, 
    clearCart, 
    getCartTotal,
    getCartCount
  } = useCart();

  if (!isCartOpen) return null;

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={() => setIsCartOpen(false)}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            <h2 className="font-bold text-lg">Shopping Cart</h2>
            <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {getCartCount()} items
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCartOpen(false)}
            className="text-white hover:bg-white/20"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <ShoppingBag className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">Your cart is empty</p>
              <p className="text-sm">Add worksheets or question banks to get started</p>
              <Button 
                onClick={() => { setIsCartOpen(false); navigate("/eduquest"); }}
                className="mt-4 bg-emerald-600 hover:bg-emerald-700"
              >
                Browse Shop
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {cartItems.map((item) => (
                <div 
                  key={item.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all hover:shadow-sm ${
                    item.product_type === "worksheet" 
                      ? "bg-blue-50 border-blue-100" 
                      : "bg-purple-50 border-purple-100"
                  }`}
                >
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    item.product_type === "worksheet" ? "bg-blue-100" : "bg-purple-100"
                  }`}>
                    {item.product_type === "worksheet" ? (
                      <FileText className="w-5 h-5 text-blue-600" />
                    ) : (
                      <BookOpen className="w-5 h-5 text-purple-600" />
                    )}
                  </div>
                  
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 text-sm truncate">
                      {item.topic_display}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.product_type === "worksheet" ? "Worksheet" : "Question Bank"} • Class {item.grade} • {item.subject_display}
                    </p>
                  </div>
                  
                  {/* Price & Remove */}
                  <div className="flex items-center gap-2">
                    <span className={`font-bold flex items-center ${
                      item.product_type === "worksheet" ? "text-blue-600" : "text-purple-600"
                    }`}>
                      <IndianRupee className="w-3 h-3" />
                      {item.price}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFromCart(item.id)}
                      className="text-gray-400 hover:text-red-500 p-1 h-7 w-7"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer - Total & Checkout */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            {/* Summary */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Subtotal ({getCartCount()} items)</span>
              <span className="text-xl font-bold text-gray-800 flex items-center">
                <IndianRupee className="w-4 h-4" />
                {getCartTotal()}
              </span>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={clearCart}
                className="flex-1 border-gray-300"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear
              </Button>
              <Button
                onClick={handleCheckout}
                className="flex-[2] bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Checkout
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <p className="text-xs text-gray-400 text-center mt-3">
              + 18% GST applicable at checkout
            </p>
          </div>
        )}
      </div>
    </>
  );
}
